import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { surface } from '../../theme';
import AnalyticsService from '../../services/AnalyticsService';
import RangePicker from '../../components/analytics/RangePicker';
import { formatDate } from '../../utils/analyticsFormat';

const POLL_MS = 3000;

export const folderKey = (group) => group.id || 'ungrouped';

/** Pulls fresh numbers from YouTube for all of the partner's connected channels. */
function SyncButton({ onFinished }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const next = await AnalyticsService.syncStatus();
      setStatus(next);
      if (next.running) {
        timer.current = setTimeout(poll, POLL_MS);
      } else if (timer.current) {
        timer.current = null;
        onFinished();
      }
    } catch (_) {
      timer.current = setTimeout(poll, POLL_MS);
    }
  }, [onFinished]);

  useEffect(() => {
    AnalyticsService.syncStatus()
      .then((next) => {
        setStatus(next);
        if (next.running) timer.current = setTimeout(poll, POLL_MS);
      })
      .catch(() => {});
    return () => clearTimeout(timer.current);
  }, [poll]);

  async function start() {
    setError('');
    try {
      setStatus(await AnalyticsService.syncNow());
      timer.current = setTimeout(poll, POLL_MS);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start the sync.');
    }
  }

  const running = status?.running;
  let note = '';
  if (error) note = error;
  else if (running) note = `Syncing ${status.done} of ${status.total} channels…`;
  else if (status?.finishedAt)
    note = `Last sync: ${status.ok} updated${status.failed ? `, ${status.failed} failed` : ''}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {note && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
          {note}
        </Typography>
      )}
      <Button
        variant="contained"
        onClick={start}
        disabled={running}
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
      >
        Sync now
      </Button>
    </Box>
  );
}

/** Range picker, the dates it covers, and the sync button. */
export function RangeToolbar({ summary, selection, onSelectionChange, reload, children }) {
  const { range } = summary;
  return (
    <Paper
      sx={{
        borderRadius: 2,
        background: surface.glass,
        boxShadow: 2,
        p: { xs: 2, md: 2.5 },
        mb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <RangePicker selection={selection} onChange={onSelectionChange} />
      {children}
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatDate(range.from)} – {formatDate(range.to)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Compared with {formatDate(range.previousFrom)} – {formatDate(range.previousTo)}.{' '}
          {range.provisional
            ? 'YouTube is still finalizing these days, so the numbers may change.'
            : 'The last 3 days are left out while YouTube finalizes them.'}
        </Typography>
      </Box>
      <SyncButton onFinished={reload} />
    </Paper>
  );
}

/** Show or hide folders. Totals, charts, and rows follow the selection. */
export function FolderFilter({ summary, hiddenFolders, onHiddenFoldersChange }) {
  const folders = summary.groups.filter((group) => group.channelCount > 0);
  if (folders.length < 2) return null;
  const shown = folders.filter((group) => !hiddenFolders.has(folderKey(group)));

  function toggle(group) {
    const next = new Set(hiddenFolders);
    if (next.has(folderKey(group))) next.delete(folderKey(group));
    else next.add(folderKey(group));
    onHiddenFoldersChange(next);
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
      {folders.map((group) => {
        const on = !hiddenFolders.has(folderKey(group));
        return (
          <Chip
            key={folderKey(group)}
            label={`${group.name} · ${group.channelCount}`}
            onClick={() => toggle(group)}
            aria-pressed={on}
            variant={on ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              color: on ? 'primary.dark' : 'white',
              bgcolor: on ? 'white' : 'transparent',
              borderColor: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: on ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.12)' },
            }}
          />
        );
      })}
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', ml: 0.5 }}>
        Showing {shown.length} of {folders.length} folders
      </Typography>
      <Button
        size="small"
        onClick={() => onHiddenFoldersChange(new Set())}
        disabled={shown.length === folders.length}
        sx={{ color: 'white', boxShadow: 'none', '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' } }}
      >
        Show all
      </Button>
    </Box>
  );
}
